import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const hourlyCross: WikiTopic = {
  slug: "ema-vwap-cross",
  title: "EMA/VWAP cross",
  summary: "The hourly EMA crossing the session-anchored VWAP.",
  group: "strategies",
  horizon: "session",
  strategyKey: "hourly_cross",
  sections: [
    {
      heading: "What it looks for",
      code: "ema_vwap_cross_bullish / ema_vwap_cross_bearish",
      blocks: [
        p(
          <>
            An EMA-<Datum>{QUOTED.HOURLY_CROSS_EMA_PERIOD}</Datum> on hourly closes crossing the
            VWAP anchored to the current session. Bullish when the EMA sat at or below VWAP on the
            previous bucket and sits above it now; bearish is the mirror.
          </>,
        ),
        p(
          <>
            It needs <Datum>{QUOTED.HOURLY_CROSS_MIN_BARS}</Datum> buckets of history, twenty times
            the EMA period, which is where the recursion has forgotten its seed.
          </>,
        ),
        p(
          <>
            Both series are indexed against the same bar array, so the previous value is the
            previous bucket even when it belongs to the previous session. That is deliberate: a
            crossing over a session boundary is handled by the bucket gate below, not by re-slicing
            the arrays.
          </>,
        ),
      ],
    },
    {
      heading: "The gates",
      blocks: [
        p(
          <>
            The gate that matters is the bucket floor. VWAP resets at the session boundary, so at
            bucket 1 it equals that bucket's typical price and sits far from a 20-hour EMA. Without
            the guard this would report a crossing every single morning, on every symbol. So it
            fires from bucket <Datum>{QUOTED.HOURLY_CROSS_MIN_BUCKET}</Datum> onward and never
            before.
          </>,
        ),
        p(
          <>
            A second guard rejects a session whose volume so far is zero. That symbol's VWAP is a
            typical price, not a VWAP, and crossing it means nothing.
          </>,
        ),
        table(
          "EMA/VWAP cross gates",
          ["Gate", "Value"],
          [
            ["EMA period", <Datum key="e">{QUOTED.HOURLY_CROSS_EMA_PERIOD}</Datum>],
            ["Earliest bucket", <Datum key="k">bucket {QUOTED.HOURLY_CROSS_MIN_BUCKET}</Datum>],
            ["Minimum history", <Datum key="b">{QUOTED.HOURLY_CROSS_MIN_BARS} buckets</Datum>],
            ["Session volume", "Must be above zero"],
          ],
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Magnitude is the distance between the EMA and VWAP in ATR, the same scaling the EMA
            cross uses. The two are meant to be read on one scale in a validation distribution, and
            they only are because both divide by the same ATR.
          </>,
        ),
        p(
          <>
            Stop is the signal bar's low on a long and its high on a short; target is twice that
            distance the other way. Same 2:1 arithmetic as the EMA cross.
          </>,
        ),
        note(
          <>
            A symbol whose ATR is null or zero returns nothing rather than throwing, for the same
            reason as the EMA cross: the runner counts a throw as a failure, and a symbol that did
            not trade is not a broken strategy.
          </>,
        ),
      ],
    },
  ],
};
